import { useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';

import { useAdminUsersList } from 'lib/hooks/queries/admin/useAdminList';
import { usePracticeSupportSettings } from 'lib/hooks/queries/practice/usePracticeSupportSettings';

import AdminSupportForm from './components/AdminSupportForm';
import UserSupportView from './components/UserSupportView';
import usePracticeSupportFormik from './lib/practiceSupportFormik';

const SupportForm = ({
  isAdmin,
  setHeaderButtonProps,
  mutateOrchestratorData,
  addFooterActions,
  updateConfigs,
  onNext,
  onPrev,
  orchestratorLoading
}) => {
  const { practiceId } = useOutletContext();
  const { data } = useAdminUsersList({
    params: { page: 1, limit: 100, withCount: true, filters: { status: [{ values: ['active'] }] } }
  });
  const queryClient = useQueryClient();

  const providers = (data?.users ?? []).map((u) => ({
    label: `${u?.f_name ?? ''} ${u?.l_name ?? ''}`.trim(),
    value: u?.id
  }));

  const { data: supportData } = usePracticeSupportSettings({
    params: { practiceId },
    dependencies: [practiceId],
    options: { enabled: !!practiceId }
  });

  const initialValues = {
    consultantId: supportData?.practiceSupport?.consultant_id,
    onboardingArchitectId: supportData?.practiceSupport?.onboarding_architect_id,
    insideRepresentativeId: supportData?.practiceSupport?.inside_representative_id,
    region: supportData?.practiceSupport?.region,
    medbillBuiltInBiller: supportData?.practiceSupport?.medbill_built_in_biller,
    medbillVirtualOfficeManager: supportData?.practiceSupport?.medbill_virtual_office_manager,
    generalNote: supportData?.practiceSupport?.general_note
  };

  const formik = usePracticeSupportFormik(initialValues, async (values) => {
    const payload = {
      ...values,
      consultantId: values.consultantId || null,
      onboardingArchitectId: values.onboardingArchitectId || null,
      insideRepresentativeId: values.insideRepresentativeId || null,
      region: values.region || null
    };
    await mutateOrchestratorData(payload);
    queryClient.invalidateQueries(['practice-support']);
  });

  useEffect(() => {
    setHeaderButtonProps(null);
    if (isAdmin) {
      addFooterActions([
        {
          text: 'Continue',
          onClick: formik.dirty ? formik.handleSubmit : onNext,
          disabled: orchestratorLoading || !formik.isValid
        }
      ]);
    } else {
      updateConfigs({
        goBackHandler: onPrev,
        actions: [
          {
            text: 'Continue',
            onClick: onNext
          }
        ]
      });
    }
  }, [orchestratorLoading, formik.dirty, formik.isValid, isAdmin]);

  if (!isAdmin) {
    return <UserSupportView supportData={supportData} />;
  }

  return <AdminSupportForm formik={formik} providers={providers} isAdmin={isAdmin} />;
};

export default SupportForm;
